import { useState, useEffect, useCallback } from "react";
import { T, F, Over, Pill, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// O. Reg. 579/05 s.14 shortfall log: detect, record that RECO was notified, link top-up, close.
// Bricks does NOT send the notice to RECO.
const cad = (cents) => new Intl.NumberFormat('en-CA', { style:'currency', currency:'CAD' }).format((cents || 0) / 100);
const day = (d) => d ? new Date(d).toLocaleString('en-CA', { month:'short', day:'numeric', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
const STATUS = { open:['warn','Open'], notified:['warn','Notified'], topped_up:['ok','Topped up'], closed:['neutral','Closed'] };
const SOURCE = { reconciliation:'Reconciliation', deal_balance:'Deal balance', account_balance:'Account balance', manual:'Manual' };

export default function TrustShortfalls({ BASE, headers, p, dark, canManage }) {
  const [data, setData] = useState(null);
  const [notifyFor, setNotifyFor] = useState(null);
  const [topUpFor, setTopUpFor] = useState(null);
  const [form, setForm] = useState({ notify_method:'phone', notify_reference:'', notes:'' });
  const [topUpId, setTopUpId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${BASE}/api/accounting/trust/shortfalls`, { headers }).then(r => r.ok ? r.json() : null).then(setData).catch(() => setData(null));
  }, [BASE, headers.Authorization]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const post = async (path, body, ok) => {
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/api/accounting/trust/shortfalls${path}`, { method:'POST', headers, body: JSON.stringify(body || {}) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'That didn’t go through');
      if (ok) toast.success(ok);
      load();
      return d;
    } catch (e) { toast.error(e.message); return null; }
    finally { setBusy(false); }
  };

  const rows = data?.shortfalls || [];
  const openCount = rows.filter(r => r.status === 'open' || r.status === 'notified' || r.status === 'topped_up').length;
  const input = { marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' };
  const small = (color) => ({ background:'none', border:`1px solid ${p.hair}`, borderRadius:2, padding:'4px 10px', cursor:'pointer', fontSize:11, fontFamily:F.mono, color });

  if (!data) return null;

  return (
    <div style={{ border:`1px solid ${p.hair}`, borderRadius:2 }}>
      <div style={{ padding:'16px var(--bx-pad)', borderBottom:`1px solid ${p.hair}`, display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <div>
          <Over color={p.ink3}>Shortfalls · O. Reg. 579/05 s.14</Over>
          <div style={{ fontSize:13, color:p.ink2, marginTop:4 }}>
            {openCount ? `${openCount} open` : 'None open'} · Record that RECO was notified — Bricks does not send the notice for you.
          </div>
        </div>
        {canManage && (
          <Btn variant="ghost" dark={dark} disabled={busy} onClick={() => post('/scan', {}, 'Scan complete')}>Scan books</Btn>
        )}
      </div>
      {rows.length === 0 ? (
        <div style={{ padding:'24px var(--bx-pad)', fontSize:13, color:p.ink3 }}>No shortfalls recorded. Signing an unbalanced reconciliation, or a negative deal/account balance on scan, opens one here.</div>
      ) : (
        rows.slice(0, 20).map((r, i) => {
          const [tone, label] = STATUS[r.status] || ['neutral', r.status];
          return (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1.1fr 1fr 1fr 1.4fr auto', gap:12, padding:'12px var(--bx-pad)', borderBottom:i === Math.min(rows.length, 20) - 1 ? 'none' : `1px solid ${p.hair}`, fontSize:13, alignItems:'center' }}>
              <div>
                <div style={{ fontFamily:F.mono, color:T.bad }}>{cad(r.amount_cents)}</div>
                <div style={{ fontSize:11, color:p.ink3, marginTop:2 }}>{SOURCE[r.source] || r.source}{r.period ? ` · ${r.period}` : ''}</div>
              </div>
              <div><Pill tone={tone} dark={dark}>{label}</Pill></div>
              <div style={{ fontSize:12, color:p.ink3 }}>{day(r.detected_at)}</div>
              <div style={{ fontSize:12, color:p.ink2 }}>
                {r.notified_at ? `Notified ${day(r.notified_at)} via ${r.notify_method}${r.notify_reference ? ` · ${r.notify_reference}` : ''}` : (r.notes || '—')}
              </div>
              <div style={{ display:'flex', gap:6, justifyContent:'flex-end', flexWrap:'wrap' }}>
                {canManage && r.status === 'open' && (
                  <button disabled={busy} style={small(T.brick)} onClick={() => { setNotifyFor(r); setForm({ notify_method:'phone', notify_reference:'', notes:'' }); }}>Notify</button>
                )}
                {canManage && r.status === 'notified' && (
                  <button disabled={busy} style={small(T.brick)} onClick={() => { setTopUpFor(r); setTopUpId(''); }}>Link top-up</button>
                )}
                {canManage && (r.status === 'notified' || r.status === 'topped_up') && (
                  <button disabled={busy} style={small(p.ink3)} onClick={() => post(`/${r.id}/close`, {}, 'Shortfall closed')}>Close</button>
                )}
              </div>
            </div>
          );
        })
      )}

      <Dialog open={!!notifyFor} onOpenChange={(o) => !o && setNotifyFor(null)}>
        <DialogContent style={{ maxWidth:480, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>Record RECO notification</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>
              Bricks does not send the notice for you. Enter how you notified the registrar and the confirmation or person spoken to.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:18 }}>
            <div><Over color={p.ink3}>Method</Over>
              <select style={input} value={form.notify_method} onChange={e => setForm({ ...form, notify_method: e.target.value })}>
                <option value="phone">Phone</option>
                <option value="email">Email</option>
                <option value="portal">RECO portal / MyWeb</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div><Over color={p.ink3}>Reference (who / confirmation #)</Over>
              <input style={input} value={form.notify_reference} onChange={e => setForm({ ...form, notify_reference: e.target.value })} placeholder="e.g. Spoke with J. Lee, case #12345"/>
            </div>
            <div><Over color={p.ink3}>Notes (optional)</Over>
              <input style={input} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop:22, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setNotifyFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={async () => {
              const ok = await post(`/${notifyFor.id}/notify`, form, 'Notification recorded');
              if (ok) setNotifyFor(null);
            }}>Save record</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!topUpFor} onOpenChange={(o) => !o && setTopUpFor(null)}>
        <DialogContent style={{ maxWidth:440, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>Link brokerage top-up</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>
              Paste the id of an approved brokerage top-up trust entry that restored the account.
            </DialogDescription>
          </DialogHeader>
          <div style={{ paddingTop:18 }}>
            <Over color={p.ink3}>Top-up entry id</Over>
            <input style={{ ...input, fontFamily:F.mono }} value={topUpId} onChange={e => setTopUpId(e.target.value)} placeholder="uuid of approved brokerage_top_up"/>
          </div>
          <DialogFooter style={{ marginTop:22, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setTopUpFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy || !topUpId.trim()} onClick={async () => {
              const ok = await post(`/${topUpFor.id}/link-top-up`, { top_up_entry_id: topUpId.trim() }, 'Top-up linked');
              if (ok) setTopUpFor(null);
            }}>Link</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
